/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect } from 'react';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';

import ApplicationLayout from 'components/EntryLayouts/ApplicationLayout';
import AuthorizationLayout from 'components/EntryLayouts/AuthorizationLayout';
import ChooseAccountType from 'components/ChooseAccountType';
import SuccessAccountRegistration from 'components/Registration/Account/SuccessAccountRegistration';
import EmailRegistration from 'components/Registration/Email';
import PasswordRecoveryStages from 'components/PasswordRecovery';

import LoginStages from 'components/Authorization';
import { getAccessExpiresOn, isTokenValid, clearLocalStorage } from 'services/authUtils';
import { ElementWrapper } from 'routes/ElementWrapper';
import {
  INITIAL_PATH_AFTER_LOGIN,
  ROUTE_PATHS,
  UNAUTHORIZED_ROUTE_PATHS,
  UNVERIFIED_ROUTE_PATHS
} from 'routes/constants';
import { CUSTOMER_PERMISSIONS } from 'components/common/constants';
import Accounts from './Accounts/Accounts';
import DashBoard from './DashBoard/DashBoard';
import NewPayment from './NewPayment/NewPayment';
import BetweenAccounts from './BetweenAccounts/BetweenAccounts';
// eslint-disable-next-line max-len
import BetweenAccountsPaymentForm from './BetweenAccounts/components/BetweenAccountsPaymentForm/BetweenAccountsPaymentForm';
import Cards from './Cards/Cards';
import CardsDashboard from './Cards/components/CardsDashboard';
import CreateNewCardForm from './Cards/components/CreateNewCardForm';
import VirtualCardSettings from './Cards/components/VirtualCardSettings';
import CardsAuthorizations from './Cards/components/CardsAuthorizations';
import { Transactions } from './Transactions/Transactions';
import Messages from './Messages/Messages';
import MessagesDashboard from './Messages/components/MessagesDashboard';
import Settings from './Settings/Settings';
import Profile from './Profile/Profile';
import { setTokenRefresh } from '../services/requestAgent';
import CurrencyExchange from './CurrencyExchange/CurrencyExchange';
import Fees from './Fees/Fees';
import Crypto from './Crypto/Crypto';
import { NotFound } from './common/NotFound/NotFound';
import i18nContext from './i18n-context';
import Representatives from './Representatives/index';
import { clearAllParamsFromLocalStorage, extractBaseUrl, saveAllParamsToLocalStorage } from './utils';
import { initializeAnalytics } from 'services/analytics';
import UnapprovedPayments from './UnapprovedPayments/UnapprovedPayments';
import Templates from './Templates/Templates';
import TemplatesTablePage from './Templates/components/TemplatesTablePage/TemplatesTablePage';
import CreateTemplatePage from './Templates/components/CreateTemplatePage/CreateTemplatePage';
import NewPaymentForm from './NewPaymentFormV2/NewPaymentForm';
import CreateNewPaymentFormPage from './NewPaymentFormV2/components/CreateNewPaymentFormPage/CreateNewPaymentFormPage';
import TestQuillEditor from './TestQuillEditor';
import CsvImport from './CsvImport/CsvImport';
import DocumentUpload from './DocumentRequest/DocumentUpload';

// Routes that operate on a customer's wallet/account and are meaningless without one
const WALLET_REQUIRED_ROUTE_PATHS = [
  ROUTE_PATHS.NEW_PAYMENT,
  ROUTE_PATHS.NEW_PAYMENT_V2,
  ROUTE_PATHS.BETWEEN_ACCOUNTS,
  ROUTE_PATHS.CARDS,
  ROUTE_PATHS.CURRENCY_EXCHANGE,
  ROUTE_PATHS.ACCOUNTS,
  ROUTE_PATHS.TRANSACTIONS,
  ROUTE_PATHS.TEMPLATES,
  ROUTE_PATHS.UNAPPROVED_PAYMENTS,
  ROUTE_PATHS.IMPORT_CSV,
  ROUTE_PATHS.CRYPTO
];

function App({ authStore, userStore, messagesStore }) {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const i18n = useContext(i18nContext);

  useEffect(() => {
    // TRN-6922: the registration magic link's query is a one-time responseId/token, not UTM tags — saving it
    // would persist the token and overwrite the visitor's stored UTM tags.
    if (searchParams.size > 0 && location.pathname !== ROUTE_PATHS.REGISTRATION_PASSWORD_SETUP) {
      clearAllParamsFromLocalStorage();
      saveAllParamsToLocalStorage(searchParams);
    }
    initializeAnalytics();
  }, []);

  useEffect(() => {
    // Handle /logout route
    if (location.pathname === '/logout') {
      clearLocalStorage();
      authStore.reset();
      // TRN-7470: replace, so browser Back from /login goes to the page the user logged out from, not /logout
      navigate(ROUTE_PATHS.LOGIN, { replace: true });
      return;
    }

    // TRN-6358: an already-authenticated user (e.g. a second tab opened on /login while another tab
    // is logged in — the token lives in the shared localStorage) must not get the login form again.
    // Re-running the password step would overwrite the live token with a limited 2FA one and log the
    // other tab out, so send them into the app instead; the effects below then route "/" to the
    // dashboard / verification page as usual. Skipped while a login is in progress in THIS tab
    // (isLoginSuccess — the 2FA step already has a token in storage and LoginStages owns the
    // post-2FA redirect) and for the registration hand-off (isUserAlreadyRegistered).
    // NB: the pre-2FA token can't be told apart here — backend issues it with scope "all" too (the
    // limitation is the "2fa" authority, not the scope). A leftover one from an interrupted login
    // therefore takes this redirect, hits 401 on the first protected call and is logged out via
    // /logout — the same bounce a plain reload of /login already produces today via loadUserData.
    if (
      isTokenValid() &&
      location.pathname === ROUTE_PATHS.LOGIN &&
      !authStore.isLoginSuccess &&
      !authStore.isUserAlreadyRegistered
    ) {
      navigate(INITIAL_PATH_AFTER_LOGIN, { replace: true });
      return;
    }

    if (
      isTokenValid() &&
      !authStore.isUserAlreadyRegistered &&
      location.pathname !== ROUTE_PATHS.REGISTRATION_CUSTOMER &&
      location.pathname !== ROUTE_PATHS.REGISTRATION_SUCCESS
    ) {
      const expiresOn = getAccessExpiresOn();
      setTokenRefresh((expiresOn - Date.now()) / 1000);

      // Fetch initial data
      if (!userStore.isUserDataFetched) {
        authStore.reset();
        userStore.loadUserData().then(() => {
          if (userStore.userData.account) {
            messagesStore.initializeData(userStore.userData.account.account_number);
          }
        });
        userStore.getAppState();
      }
    }

    // Need to do some logic (API) to check user authorization status
    // Special handling for document-request - it has its own auth/error handling (token or
    // logged-in customerNumber) and must never be bounced to login/verification by these guards.
    const isDocumentRequestPage = location.pathname.includes('/document-request');

    if (
      !isTokenValid() &&
      !Object.keys(UNAUTHORIZED_ROUTE_PATHS).filter((path) =>
        location.pathname.includes(UNAUTHORIZED_ROUTE_PATHS[path])
      ).length &&
      !isDocumentRequestPage
    ) {
      navigate(ROUTE_PATHS.LOGIN);
    }
  }, [navigate, location.pathname]);

  useEffect(() => {
    // Auto-logout if user is logged in and tries to access password recovery with token
    if (
      isTokenValid() &&
      location.pathname.includes('/password_recovery/email/confirm/') &&
      location.pathname.split('/').length > 4 // Ensure there's a token in the path
    ) {
      clearLocalStorage();
      authStore.reset();
      userStore.reset();
      // Don't navigate, let the user stay on password recovery page
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    /* Redirection for unverified account.
     *  All routes (except UNVERIFIED_ROUTE_PATHS, UNAUTHORIZED_ROUTE_PATHS ) will be redirect to /dashboard
     */
    // Don't redirect document-request to verification - it has its own auth/error handling
    const isDocumentRequestPage = location.pathname.includes('/document-request');

    if (userStore.isUserDataFetched && !isDocumentRequestPage) {
      const baseUrl = extractBaseUrl(location.pathname);
      if (
        !Object.values({ ...UNVERIFIED_ROUTE_PATHS, ...UNAUTHORIZED_ROUTE_PATHS }).includes(location.pathname) &&
        baseUrl !== ROUTE_PATHS.MESSAGES &&
        (!userStore.userData.account ||
          (userStore.userData?.account && userStore.isCustomerVerified !== null && !userStore.isCustomerVerified))
      ) {
        navigate(ROUTE_PATHS.VERIFICATION);
      } else if (
        (userStore.isCustomerVerified && baseUrl === ROUTE_PATHS.VERIFICATION) ||
        location.pathname === ROUTE_PATHS.ROOT
      ) {
        navigate(ROUTE_PATHS.DASHBOARD);
      }
    }
  }, [navigate, location.pathname, userStore.isCustomerVerified, userStore.isUserDataFetched]);

  useEffect(() => {
    // Only pages that operate on a wallet require the customer to have at least one account.
    // Other tabs (Messages, Settings, Profile, Fees, Representatives, etc.) must stay reachable
    // even for a freshly verified customer with zero accounts yet.
    const baseUrl = extractBaseUrl(location.pathname);

    if (
      userStore.isCustomerVerified &&
      userStore.userAccounts.length === 0 &&
      WALLET_REQUIRED_ROUTE_PATHS.includes(baseUrl)
    ) {
      navigate(ROUTE_PATHS.DASHBOARD);
    }
  }, [userStore.userAccounts, location.pathname]);

  // Without any permissions access will be blocked for all shared accounts
  const isUserHasPermissions = userStore?.hasPermissions;

  return (
    <Routes>
      <Route path={ROUTE_PATHS.ROOT} element={<ApplicationLayout />}>
        <Route path='/test-quill' element={<TestQuillEditor />} />
        <Route
          path={ROUTE_PATHS.DASHBOARD}
          element={
            <ElementWrapper
              isHasPermission={isUserHasPermissions([CUSTOMER_PERMISSIONS.READ_ACCOUNT])}
              component={DashBoard}
              headerText={i18n.getMessage('menu.dashboard.text')}
            />
          }
        />
        <Route
          path={ROUTE_PATHS.VERIFICATION}
          element={
            <ElementWrapper
              isHasPermission={isUserHasPermissions()}
              component={DashBoard}
              headerText={i18n.getMessage('navigation.verification.text')}
            />
          }
        />
        <Route
          path={ROUTE_PATHS.MESSAGES}
          element={
            <ElementWrapper
              isHasPermission={isUserHasPermissions([
                CUSTOMER_PERMISSIONS.READ_MESSAGES,
                CUSTOMER_PERMISSIONS.CREATE_MESSAGE
              ])}
              component={Messages}
              headerText={i18n.getMessage('navigation.messages.text')}
            />
          }
        >
          <Route
            index
            element={
              <ElementWrapper
                isHasPermission={isUserHasPermissions([
                  CUSTOMER_PERMISSIONS.READ_MESSAGES,
                  CUSTOMER_PERMISSIONS.CREATE_MESSAGE
                ])}
                component={MessagesDashboard}
                headerText={i18n.getMessage('navigation.messages.text')}
              />
            }
          />
          <Route
            path={ROUTE_PATHS.MESSAGES_TOPIC}
            element={
              <ElementWrapper
                isHasPermission={isUserHasPermissions([
                  CUSTOMER_PERMISSIONS.READ_MESSAGES,
                  CUSTOMER_PERMISSIONS.CREATE_MESSAGE
                ])}
                component={MessagesDashboard}
                headerText={i18n.getMessage('navigation.messages.text')}
              />
            }
          />
          <Route
            path={ROUTE_PATHS.MESSAGES_CREATE_NEW_TOPIC}
            element={
              <ElementWrapper
                isHasPermission={isUserHasPermissions([
                  CUSTOMER_PERMISSIONS.READ_MESSAGES,
                  CUSTOMER_PERMISSIONS.CREATE_MESSAGE
                ])}
                component={MessagesDashboard}
                headerText={i18n.getMessage('navigation.messages.text')}
              />
            }
          />
        </Route>
        <Route
          path={ROUTE_PATHS.SETTINGS}
          element={
            <ElementWrapper
              isHasPermission={isUserHasPermissions(CUSTOMER_PERMISSIONS.READ_ACCOUNT)}
              component={Settings}
              headerText={i18n.getMessage('navigation.settings.text')}
            />
          }
        />
        <Route
          path={ROUTE_PATHS.PROFILE}
          element={
            <ElementWrapper
              isHasPermission={isUserHasPermissions([CUSTOMER_PERMISSIONS.READ_PROFILE])}
              component={Profile}
              headerText={i18n.getMessage('navigation.profile.text')}
            />
          }
        />
        {userStore.isCustomerVerified && (
          <>
            <Route
              path={ROUTE_PATHS.NEW_PAYMENT}
              element={
                <ElementWrapper
                  isHasPermission={isUserHasPermissions([
                    CUSTOMER_PERMISSIONS.CREATE_TRANSACTION,
                    CUSTOMER_PERMISSIONS.CREATE_PREPARED_TRANSACTION
                  ])}
                  component={NewPayment}
                  headerText={i18n.getMessage('container.sendMoney')}
                />
              }
            />
            {userStore.isNewPaymentFormEnabled() && (
              <Route
                path={ROUTE_PATHS.NEW_PAYMENT_V2}
                element={
                  <ElementWrapper
                    isHasPermission={isUserHasPermissions([
                      CUSTOMER_PERMISSIONS.CREATE_TRANSACTION,
                      CUSTOMER_PERMISSIONS.CREATE_PREPARED_TRANSACTION
                    ])}
                    component={NewPaymentForm}
                    headerText={i18n.getMessage('container.newPaymentV2')}
                  />
                }
              >
                <Route
                  index
                  element={
                    <ElementWrapper
                      isHasPermission={isUserHasPermissions([
                        CUSTOMER_PERMISSIONS.CREATE_TRANSACTION,
                        CUSTOMER_PERMISSIONS.CREATE_PREPARED_TRANSACTION
                      ])}
                      component={CreateNewPaymentFormPage}
                      headerText={i18n.getMessage('container.newPaymentV2')}
                    />
                  }
                />
              </Route>
            )}
            <Route
              path={ROUTE_PATHS.BETWEEN_ACCOUNTS}
              element={
                <ElementWrapper
                  isHasPermission={isUserHasPermissions([CUSTOMER_PERMISSIONS.CREATE_INTERNAL_TRANSACTION])}
                  component={BetweenAccounts}
                  headerText={i18n.getMessage('container.betweenAccounts')}
                />
              }
            >
              <Route
                index
                element={
                  <ElementWrapper
                    isHasPermission={isUserHasPermissions([CUSTOMER_PERMISSIONS.CREATE_INTERNAL_TRANSACTION])}
                    component={BetweenAccountsPaymentForm}
                    headerText={i18n.getMessage('container.betweenAccounts')}
                  />
                }
              />
            </Route>
            {userStore.isCardsEnabled() && (
              <Route
                path={ROUTE_PATHS.CARDS}
                element={
                  <ElementWrapper
                    isHasPermission={isUserHasPermissions([CUSTOMER_PERMISSIONS.READ_CARDS])}
                    component={Cards}
                    headerText={i18n.getMessage('container.cards')}
                  />
                }
              >
                <Route
                  index
                  element={
                    <ElementWrapper
                      isHasPermission={isUserHasPermissions([CUSTOMER_PERMISSIONS.READ_CARDS])}
                      component={CardsDashboard}
                      headerText={i18n.getMessage('container.cards')}
                    />
                  }
                />
                <Route
                  path={ROUTE_PATHS.CARDS_CREATE}
                  element={
                    <ElementWrapper
                      isHasPermission={isUserHasPermissions([CUSTOMER_PERMISSIONS.CREATE_CARDS])}
                      component={CardsDashboard}
                      headerText={i18n.getMessage('container.createNewCard')}
                    />
                  }
                />
                <Route
                  path={ROUTE_PATHS.CARDS_CREATE_VIRTUAL}
                  element={
                    <ElementWrapper
                      isHasPermission={isUserHasPermissions([CUSTOMER_PERMISSIONS.CREATE_CARDS])}
                      component={CreateNewCardForm}
                      headerText={i18n.getMessage('container.createVirtualCard')}
                    />
                  }
                />
                <Route
                  path={ROUTE_PATHS.CARD_SETTINGS}
                  element={
                    <ElementWrapper
                      isHasPermission={isUserHasPermissions([CUSTOMER_PERMISSIONS.CREATE_CARDS])}
                      component={VirtualCardSettings}
                      headerText={i18n.getMessage('container.cardSettings')}
                    />
                  }
                />
                <Route
                  path={ROUTE_PATHS.CARDS_AUTHORIZATIONS}
                  element={
                    <ElementWrapper
                      isHasPermission={isUserHasPermissions([CUSTOMER_PERMISSIONS.READ_CARDS])}
                      component={CardsAuthorizations}
                      headerText={i18n.getMessage('container.cardsAuthorizations')}
                    />
                  }
                />
              </Route>
            )}

            <Route
              path={ROUTE_PATHS.CURRENCY_EXCHANGE}
              element={
                <ElementWrapper
                  isHasPermission={isUserHasPermissions([CUSTOMER_PERMISSIONS.CREATE_CURRENCY_EXCHANGE])}
                  component={CurrencyExchange}
                  headerText={i18n.getMessage('container.exchange')}
                />
              }
            />
            <Route
              path={ROUTE_PATHS.ACCOUNTS}
              element={
                <ElementWrapper
                  isHasPermission={isUserHasPermissions([CUSTOMER_PERMISSIONS.READ_ACCOUNT])}
                  component={Accounts}
                  headerText={i18n.getMessage('container.accounts')}
                />
              }
            />
            <Route
              path={ROUTE_PATHS.TRANSACTIONS}
              element={
                <ElementWrapper
                  isHasPermission={isUserHasPermissions([
                    CUSTOMER_PERMISSIONS.READ_ACCOUNT,
                    CUSTOMER_PERMISSIONS.CREATE_REPORT
                  ])}
                  component={Transactions}
                  headerText={i18n.getMessage('container.Transactions')}
                />
              }
            />
            <Route
              path={ROUTE_PATHS.TEMPLATES}
              element={
                <ElementWrapper
                  isHasPermission={isUserHasPermissions([CUSTOMER_PERMISSIONS.CREATE_PREPARED_TRANSACTION])}
                  component={Templates}
                  headerText={i18n.getMessage('container.savedTemplates')}
                />
              }
            >
              <Route
                index
                element={
                  <ElementWrapper
                    isHasPermission={isUserHasPermissions([CUSTOMER_PERMISSIONS.CREATE_PREPARED_TRANSACTION])}
                    component={TemplatesTablePage}
                    headerText={i18n.getMessage('container.savedTemplates')}
                  />
                }
              />
              <Route
                path={ROUTE_PATHS.TEMPLATES_CREATE}
                element={
                  <ElementWrapper
                    isHasPermission={isUserHasPermissions([CUSTOMER_PERMISSIONS.CREATE_PREPARED_TRANSACTION])}
                    component={CreateTemplatePage}
                    headerText={i18n.getMessage('container.createTemplate')}
                  />
                }
              />
            </Route>
            <Route
              path={ROUTE_PATHS.UNAPPROVED_PAYMENTS}
              element={
                <ElementWrapper
                  isHasPermission={isUserHasPermissions([CUSTOMER_PERMISSIONS.CREATE_PREPARED_TRANSACTION])}
                  component={UnapprovedPayments}
                  headerText={i18n.getMessage('container.forApproval')}
                />
              }
            />
            {userStore.isCsvImportEnabled() && (
              <Route
                path={ROUTE_PATHS.IMPORT_CSV}
                element={
                  <ElementWrapper
                    isHasPermission={isUserHasPermissions([CUSTOMER_PERMISSIONS.CREATE_PREPARED_TRANSACTION])}
                    component={CsvImport}
                    headerText={i18n.getMessage('container.csvImport')}
                  />
                }
              />
            )}
          </>
        )}

        {userStore.isFeesEnabled() && (
          <Route
            path={ROUTE_PATHS.FEES}
            element={
              <ElementWrapper
                isHasPermission={isUserHasPermissions([CUSTOMER_PERMISSIONS.READ_FEES])}
                component={Fees}
                headerText={i18n.getMessage('container.fees')}
              />
            }
          />
        )}
        {userStore.isRepresentativesEnabled() && (
          <Route
            path={ROUTE_PATHS.REPRESENTATIVES}
            element={
              <ElementWrapper
                isHasPermission={isUserHasPermissions()}
                component={Representatives}
                headerText={i18n.getMessage('container.representatives')}
              />
            }
          />
        )}
        <Route
          path={ROUTE_PATHS.CRYPTO}
          element={
            <ElementWrapper
              isHasPermission={isUserHasPermissions()}
              component={Crypto}
              headerText={i18n.getMessage('container.crypto')}
            />
          }
        />
      </Route>
      <Route path={ROUTE_PATHS.PASSWORD_RECOVERY} element={<ElementWrapper component={AuthorizationLayout} />}>
        <Route index element={<ElementWrapper component={PasswordRecoveryStages} />} />
        <Route path={'email/confirm/:tokenId'} element={<ElementWrapper component={PasswordRecoveryStages} />} />
      </Route>
      <Route path={ROUTE_PATHS.DOCUMENT_REQUEST} element={<DocumentUpload />} />
      <Route path={ROUTE_PATHS.DOCUMENT_REQUEST_WITH_ID} element={<DocumentUpload />} />
      {/*
        LOGIN is the one auth route NOT wrapped in AuthorizationLayout — the redesigned LoginStages
        component (components/Authorization) owns its whole page (two-column layout), see
        docs/specs/TRN-6921_email_login/spec.md §7.6/§8.
      */}
      <Route path={ROUTE_PATHS.LOGIN} element={<ElementWrapper component={LoginStages} />} />
      {/*
        TRN-6922: REGISTRATION (and its magic-link Confirm-step deep link) also bypass
        AuthorizationLayout now, same reasoning as LOGIN above — EmailRegistration
        (components/Registration/Email) owns its whole page. Old components/Registration/User is
        unreferenced starting this route swap and gets deleted once the new flow is fully verified,
        see docs/specs/TRN-6922_email_registration/spec.md §6.
      */}
      <Route path={ROUTE_PATHS.REGISTRATION} element={<ElementWrapper component={EmailRegistration} />} />
      <Route
        path={ROUTE_PATHS.REGISTRATION_PASSWORD_SETUP}
        element={<ElementWrapper component={EmailRegistration} isMagicLink={true} />}
      />
      {/*
        TRN-7388: REGISTRATION_CUSTOMER now renders the shared ChooseAccountType screen (also used at
        /verification) instead of the old AccountRegistration flow — see
        docs/specs/TRN-7388_customer_creation/spec.md §13. The old /registration/customer/personal and
        /registration/customer/company routes (full-detail Personal/Business forms) are gone entirely:
        customer details are now collected by the Sumsub SDK after ChooseAccountType creates an empty
        customer, not by a form here. components/Registration/Account (AccountRegistration,
        CustomerRegistrationTypeChoosing, Personal, Business) is unreferenced starting this route swap and
        can be deleted once this replacement is verified in production.
        TRN-7583: this page is for a logged-in user but lives outside ApplicationLayout, where the inactivity
        watcher is mounted — AuthorizationLayout mounts it here (isSessionTimeoutEnabled) so the session ends on
        this page as everywhere else.
      */}
      <Route path={ROUTE_PATHS.REGISTRATION_CUSTOMER} element={<AuthorizationLayout isSessionTimeoutEnabled />}>
        <Route index element={<ElementWrapper component={ChooseAccountType} />} />
      </Route>
      <Route path={ROUTE_PATHS.REGISTRATION_SUCCESS} element={<AuthorizationLayout />}>
        <Route index element={<ElementWrapper component={SuccessAccountRegistration} isRegistration={true} />} />
      </Route>
      {userStore.isCustomerVerified && <Route path={'*'} element={<NotFound />} />}
    </Routes>
  );
}

App.propTypes = {
  authStore: MobXPropTypes.observableObject,
  userStore: MobXPropTypes.observableObject,
  messagesStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  authStore: stores.authStore,
  userStore: stores.userStore,
  messagesStore: stores.messagesStore
}))(observer(App));
